import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { DialogData } from '@ebw-monorepo/modal';
import { API_URLS, ApiUrlService } from 'libs/ng-components/utils/src/lib/services/api-url.service';
import { Feature } from 'ol';
import { FeatureSelectService } from '../../../../services/feature-select.service';
import { LayerStylesService } from '../../../../services/layer-styles.service';
import { MapService } from '../../../../services/map.service';
import { PsaService } from '../../../../services/psa.service';

@Component({
  selector: 'app-psa-element-detail',
  templateUrl: './psa-element-detail.component.html',
  styleUrls: ['./psa-element-detail.component.scss']
})
export class PsaElementDetailComponent implements OnInit, OnDestroy {
  @Input() public isDialog = true;
  @Output() public detailRequested: EventEmitter<Feature> = new EventEmitter();
  @Input() isPSAEnabled = true;
  geoFeatureStyleHighlight = null;

  private _data: { key: any; value: any }[];

  private _sorgenti: { search_term: string; urn: string; geom: any }[];

  get sorgenti(): any {
    return this._sorgenti;
  }

  get data(): any {
    return this._data;
  }

  urn = '';
  id = '';

  @Input() set data(data: any) {
    if (!data) {
      return;
    }
    this.urn = data.find((el: any) => el.key === 'urn').value;
    // id interno del nostro sistema
    this.id = this.urn.split('V')[4].split('Z')[1];
    // recupero la configurazione basandomi sull'urn
    const config = this._featureSelectService.getConfigDataFromUrn(this.urn);
    // Se non c'è uso una funzione esterna pre esistente
    if (!config?.config_collection) {
      let { properties } = this._featureSelectService.parseDataForReportDialogUI(data);
      this._data = properties;
      return;
    }

    // Recupero le features selezionate
    const features = this._featureSelectService.featuresSelected;
    // Seleziono quella di cui mostrare il dettaglio
    const feature = features.find((f: Feature) => f.getId() === this.urn);
    if (!feature) {
      return;
    }
    // Estraggo le proprietà
    const properties = feature.getProperties();
    // Controllo la tipologia dell'elemento e ne recupero le props
    const isSchemaIdropotabile = this.isSchemaIdropotabileObject();
    const propsToDisplay = isSchemaIdropotabile
      ? this.getSchemaIdropotabileProperties(properties, config)
      : this.getReteIdricaProperties(properties, config);

    // Gestisco la relazione wsz-sorgenti
    const sorgenti = this.getSorgentiProps(properties);
    this._sorgenti = sorgenti;
    this._data = propsToDisplay;
  }

  // --------------------------------------------------------------------------------------------------------------------

  constructor(
    @Optional() public dialogRef: MatDialogRef<PsaElementDetailComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public matData: DialogData,
    private _featureSelectService: FeatureSelectService,
    private _psaService: PsaService,
    private _mapService: MapService,
    private _apiUrlService: ApiUrlService,
    private _layerStyleService: LayerStylesService
  ) {}

  // --------------------------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    if (this.matData && this.isDialog) {
      this.data = this.matData['data'];
    }

    this.geoFeatureStyleHighlight = this._layerStyleService.buildFeatureStyleFromId('HighlightFeatureFill');
  }

  ngOnDestroy(): void {
    this._mapService.clearAllFeaturesFromTemporaryLayer(true);
  }

  // --------------------------------------------------------------------------------------------------------------------

  getReteIdricaProperties(properties: any, config: any) {
    // Spalmo le proprietà originali dell'oggetto se annidate in una prop
    const parsedProperties = { ...properties, ...properties.additional_info };
    // elimino la prop da cui ho estratto le altre
    delete parsedProperties.additional_info;

    // Recupero i campi della collection
    const collectionFields = config?.config_collection?.fields;

    if (!collectionFields) {
      const keys = Object.keys(parsedProperties);
      return keys.map((k) => ({ key: k, value: parsedProperties[k] || ' - ' }));
    }

    const propsToDisplay = collectionFields
      .filter((f) => f.visible)
      .map((field) => {
        const value = parsedProperties[field.name];
        return { key: field.external_name, value: value || ' - ' };
      });

    return propsToDisplay;
  }

  // --------------------------------------------------------------------------------------------------------------------

  getSchemaIdropotabileProperties(properties: any, config: any) {
    const reteIdricaElements = properties.rete_idrica;

    const parsedReteIdricaElements = reteIdricaElements?.map((ele) => {
      const urn = ele.urn;
      const config = this._featureSelectService.getConfigDataFromUrn(urn);
      const propsToDIsplay = this.getReteIdricaProperties(ele, config);
      return propsToDIsplay;
    });

    properties.rete_idrica = parsedReteIdricaElements;

    const collectionFields = config?.config_collection?.fields;

    // Recupero i campi della collection
    const propsToDisplay = collectionFields
      .filter((f) => f.visible)
      .map((field) => {
        const value = properties[field.name];
        return { key: field.external_name, value: value || ' - ' };
      });

    return propsToDisplay;
  }

  // --------------------------------------------------------------------------------------------------------------------

  getSorgentiProps(properties: any) {
    const sorgenti = properties?.psa?.sorgenti;
    if (!sorgenti?.length) {
      return [];
    }

    const sorgentiElements = sorgenti.map((ele) => ({
      search_term: ele.search_term,
      urn: ele.urn,
      geom: ele.geom
    }));

    return sorgentiElements;
  }

  // --------------------------------------------------------------------------------------------------------------------

  isArray(value: unknown) {
    return value instanceof Array;
  }

  isObject(value: unknown) {
    return typeof value === 'object' && !Array.isArray(value) && value !== null;
  }

  // --------------------------------------------------------------------------------------------------------------------

  isSchemaIdropotabileObject = () => {
    return this._psaService.isUrnSchemaIdropotabile(this.urn);
  };

  // --------------------------------------------------------------------------------------------------------------------

  showDetail(item) {
    const featureCollection = {
      type: 'FeatureCollection',
      features: [
        {
          id: item.urn,
          type: 'Feature',
          geometry: safeJsonClone(item.geom),
          properties: {
            ...item
          }
        }
      ]
    };
    this._featureSelectService.featuresSelected = [];
    this._featureSelectService.parseSelectedFeatures(featureCollection, this._apiUrlService.createUrl(API_URLS.MAP.CORE.SELECT_BY_URN));
    this._featureSelectService.notifyFeaturesSelectedChange();
  }

  // --------------------------------------------------------------------------------------------------------------------

  evidence(item) {
    this._mapService.clearAllFeaturesFromTemporaryLayer(true);

    const clone = safeJsonClone(item);
    clone.geom.coordinates = this._mapService.transformGeometryCordsFrom4326To3857(clone.geom.coordinates);
    this._mapService.addFeatureToTemporaryLayer(clone.geom, this.geoFeatureStyleHighlight, true, false);
    this._mapService.centerMapOnGeometry(clone.geom);
  }
}
